/**
 * @mcp-testing/server-tester
 *
 * Playwright-based testing framework for MCP servers
 *
 * @packageDocumentation
 */

// Config
export type {
  MCPConfig,
  MCPHostCapabilities,
  MCPAuthConfig,
  MCPOAuthConfig,
} from './config/mcpConfig.js';
export {
  MCPConfigSchema,
  validateMCPConfig,
  isStdioConfig,
  isHttpConfig,
} from './config/mcpConfig.js';

// Auth
export type {
  StoredTokens,
  StoredClientInfo,
  StoredOAuthState,
  OAuthSetupConfig,
  TokenResult,
} from './auth/types.js';
export {
  PlaywrightOAuthClientProvider,
  type PlaywrightOAuthClientProviderConfig,
} from './auth/oauthClientProvider.js';
export {
  createTokenAuthHeaders,
  validateAccessToken,
  isTokenExpired,
  isTokenExpiringSoon,
} from './auth/tokenAuth.js';
export {
  performOAuthSetup,
  performOAuthSetupIfNeeded,
} from './auth/setupOAuth.js';

// Discovery (RFC 9728)
export {
  discoverProtectedResource,
  discoverAuthorizationServer,
  DiscoveryError,
  MCP_PROTOCOL_VERSION,
  type ProtectedResourceMetadata,
  type ProtectedResourceDiscoveryResult,
} from './auth/discovery.js';

// Token Storage
export {
  loadTokens,
  hasValidTokens,
  injectTokens,
  loadTokensFromEnv,
  ENV_VAR_NAMES,
  type StoredServerMetadata,
} from './auth/storage.js';

// CLI OAuth
export {
  CLIOAuthClient,
  type CLIOAuthClientConfig,
  type CLIOAuthResult,
} from './auth/cli.js';

// MCP Client
export {
  createMCPClientForConfig,
  closeMCPClient,
  type CreateMCPClientOptions,
} from './mcp/clientFactory.js';

// Response Normalization
export type { ContentBlock, NormalizedToolResponse } from './mcp/response.js';
export { normalizeToolResponse, extractText } from './mcp/response.js';

// Assertions - Matchers (primary API)
// The extended expect with MCP tool matchers is exported via fixtures
// Use: import { expect } from '@mcp-testing/server-tester'

// Assertions - Validators (for programmatic use)
export {
  validateResponse,
  validateSchema,
  validateText,
  validatePattern,
  validateError,
  validateSize,
  getResponseSizeBytes,
} from './assertions/validators/index.js';

export type {
  ValidationResult,
  TextValidatorOptions,
  SizeValidatorOptions,
  SchemaValidatorOptions,
  PatternValidatorOptions,
  SnapshotSanitizer,
  BuiltInSanitizer,
  RegexSanitizer,
  FieldRemovalSanitizer,
  SchemaRegistry,
} from './assertions/validators/types.js';

export type {
  JudgeMatcherOptions,
  ToolPredicate,
  PredicateResult,
} from './assertions/matchers/types.js';

// Fixtures
export type {
  MCPFixtureApi,
  MCPFixtureOptions,
  AuthType,
} from './mcp/fixtures/mcpFixture.js';
export { createMCPFixture } from './mcp/fixtures/mcpFixture.js';
export { test, expect } from './fixtures/mcp.js';

// Canonical Types (single source of truth)
export type {
  ResultSource,
  ExpectationType,
  ExpectationBreakdown,
  ExpectationResultMap,
} from './types/index.js';

// Eval Dataset
export type {
  EvalCase,
  EvalDataset,
  EvalExpectBlock,
  SerializedEvalDataset,
  EvalMode,
} from './evals/datasetTypes.js';
export {
  EvalCaseSchema,
  EvalDatasetSchema,
  validateEvalCase,
  validateEvalDataset,
} from './evals/datasetTypes.js';

// Eval Loader
export type { LoadDatasetOptions } from './evals/datasetLoader.js';
export {
  loadEvalDataset,
  loadEvalDatasetFromObject,
} from './evals/datasetLoader.js';

// Eval Runner
export type {
  EvalContext,
  EvalExpectationResult,
  EvalCaseResult,
  EvalRunnerResult,
  EvalRunnerOptions,
} from './evals/evalRunner.js';
export { runEvalDataset, runEvalCase } from './evals/evalRunner.js';

// LLM Host Simulation
export type {
  LLMProvider,
  LLMHostConfig,
  LLMToolCall,
  LLMHostSimulationResult,
  LLMHostSimulator,
  ExpectedToolCall,
  ToolCallValidationResult,
  ToolCallValidator,
} from './evals/llmHost/index.js';
export {
  simulateLLMHost,
  isProviderAvailable,
  getMissingDependencyMessage,
  createToolCallValidator,
} from './evals/llmHost/index.js';

// Judge
export type {
  JudgeConfig,
  Judge,
  JudgeResult,
  UsageMetrics,
  ProviderKind,
} from './judge/judgeTypes.js';
export { createJudge } from './judge/judgeClient.js';

// Conformance
export type {
  MCPConformanceOptions,
  MCPConformanceResult,
  MCPConformanceCheck,
  MCPConformanceRaw,
} from './spec/conformanceChecks.js';
export { runConformanceChecks } from './spec/conformanceChecks.js';

// Reporter
export type {
  MCPEvalReporterConfig,
  MCPEvalRunData,
  MCPEvalHistoricalSummary,
  MCPConformanceResultData,
  MCPServerCapabilitiesData,
  MCPEvalData,
} from './reporters/types.js';
